import React, { useRef, useState } from "react";
import { Upload, FileText, X } from "lucide-react";

export default function DropZone({
  file,
  files,
  onChange,
  multiple = false,
  accept = ".pdf,application/pdf",
  hint = "Trascina qui il PDF oppure clicca per selezionare",
  testId = "dropzone",
}) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const isPdf = (f) =>
    f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");

  const handleFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const arr = Array.from(fileList);
    const filtered =
      accept && accept.includes("pdf") ? arr.filter(isPdf) : arr;
    if (filtered.length === 0) return;
    if (multiple) {
      const existing = Array.isArray(files) ? files : [];
      // dedupe by name+size
      const seen = new Set(existing.map((f) => `${f.name}::${f.size}`));
      const merged = [
        ...existing,
        ...filtered.filter((f) => !seen.has(`${f.name}::${f.size}`)),
      ];
      onChange(merged);
    } else {
      onChange(filtered[0]);
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFiles(e.dataTransfer?.files);
  };

  const clearAll = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(multiple ? [] : null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeAt = (idx, e) => {
    e.stopPropagation();
    e.preventDefault();
    const next = (files || []).filter((_, i) => i !== idx);
    onChange(next);
  };

  const list = multiple ? (Array.isArray(files) ? files : []) : file ? [file] : [];
  const hasFiles = list.length > 0;

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={onDragOver}
      onDragEnter={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`border-2 border-dashed rounded-xl p-4 cursor-pointer transition-all ${
        isDragging
          ? "border-blue-500 bg-blue-50 scale-[1.01]"
          : hasFiles
          ? "border-blue-400 bg-blue-50/40"
          : "border-slate-300 hover:border-blue-400 hover:bg-slate-50/60"
      }`}
      data-testid={testId}
      role="button"
      tabIndex={0}
    >
      {hasFiles ? (
        <div className="space-y-2">
          {list.map((f, idx) => (
            <div
              key={`${f.name}-${f.size}-${idx}`}
              className="flex items-center gap-3 bg-white/70 rounded-lg px-3 py-2 border border-slate-100"
              data-testid={`${testId}-item-${idx}`}
            >
              <FileText className="text-blue-600 flex-shrink-0" size={18} />
              <div className="text-left min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 truncate">
                  {f.name}
                </div>
                <div className="text-xs text-slate-500">
                  {(f.size / 1024).toFixed(0)} KB · PDF
                </div>
              </div>
              <button
                type="button"
                onClick={(e) =>
                  multiple ? removeAt(idx, e) : clearAll(e)
                }
                className="text-slate-400 hover:text-red-500 p-1"
                title="Rimuovi"
                data-testid={`${testId}-remove-${idx}`}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1">
            <div className="text-xs text-slate-500">
              {multiple
                ? `${list.length} file selezionati · clicca o trascina per aggiungerne altri`
                : "Clicca per sostituire"}
            </div>
            {multiple && list.length > 1 && (
              <button
                type="button"
                onClick={clearAll}
                className="text-xs text-red-500 hover:text-red-600 font-medium"
                data-testid={`${testId}-clear-all`}
              >
                Rimuovi tutti
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-2">
          <Upload
            className={`mx-auto mb-2 transition-colors ${
              isDragging ? "text-blue-600" : "text-slate-400"
            }`}
            size={26}
          />
          <div className="text-sm font-medium text-slate-700">
            {isDragging ? "Rilascia i file qui" : hint}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {multiple ? "Puoi selezionare più file .pdf" : "Solo file .pdf"}
          </div>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        hidden
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testId}-input`}
      />
    </div>
  );
}
